import { useState } from "react";
import { Wrench, PlugsConnected, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * MaintainX integration shell — UI-only for now.
 * Connect flow, per-property push toggles, and a live audit queue.
 * Actual API write-mirror lands next session once the college confirms MaintainX.
 */
export default function AdminMaintainX() {
  const [apiKey, setApiKey] = useState("");
  const [pushEnabled, setPushEnabled] = useState({});
  const properties = [
    { slug: "chew-flats", name: "Chew Flats", subtitle: "LaSalle · 43 units", live: true },
    { slug: "wister-apts", name: "Wister Apts", subtitle: "8 units", live: false },
    { slug: "raza-homes", name: "Raza Homes", subtitle: "12 units", live: false },
  ];
  const save = () => {
    if (!apiKey.trim()) { toast.error("Paste your MaintainX API key first"); return; }
    toast.info("Stored locally — write-mirror activation ships next session");
  };
  return (
    <div className="p-4 max-w-4xl mx-auto space-y-4" data-testid="maintainx-page">
      <div>
        <div className="text-[10px] font-black uppercase tracking-widest text-cyan-700 flex items-center gap-1">
          <Wrench weight="fill" size={12}/> External CMMS
        </div>
        <h1 className="text-2xl font-black tracking-tight">MaintainX Write-Mirror</h1>
        <p className="text-sm text-slate-600 mt-1 max-w-2xl">
          One-way push from Field Radar → MaintainX. Every submitted request auto-creates
          a MaintainX work order with photos, notes, unit, and priority. Never mutates their
          board unless you tap Push manually — same low-risk pattern we use for ClickUp.
        </p>
      </div>

      <div className="p-3 border-2 border-amber-500 bg-amber-50 rounded flex items-start gap-2" data-testid="maintainx-preview-banner">
        <Warning weight="fill" size={18} className="text-amber-700 flex-shrink-0 mt-0.5"/>
        <div className="text-xs">
          <b className="uppercase tracking-widest text-amber-900">Preview mode</b>
          <div className="text-amber-900 mt-0.5">
            Live push is <b>OFF</b> until the college confirms MaintainX as their CMMS. You can
            paste an API key and pre-configure per-property push rules now — the write path
            activates in one line of code when you say go.
          </div>
        </div>
      </div>

      <div className="p-4 border-2 border-slate-300 bg-white rounded space-y-3">
        <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1">
          <PlugsConnected weight="fill" size={12}/> Connect
        </div>
        <label className="block">
          <div className="text-xs font-black uppercase tracking-widest text-slate-600 mb-1">MaintainX API Key</div>
          <input value={apiKey} onChange={(e) => setApiKey(e.target.value)}
            data-testid="maintainx-key-input" type="password"
            placeholder="mnx_live_…"
            className="w-full h-10 px-3 border-2 border-slate-300 rounded font-mono text-sm"/>
          <div className="text-[10px] text-slate-500 mt-1">
            Get one at MaintainX → Settings → API. Read/write scope.
          </div>
        </label>
        <button onClick={save} data-testid="maintainx-save-btn"
          className="h-10 px-4 bg-cyan-600 hover:bg-cyan-700 text-white font-black uppercase text-xs tracking-widest rounded">
          Save Key (Preview)
        </button>
      </div>

      <div className="p-4 border-2 border-slate-300 bg-white rounded space-y-3">
        <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Per-Property Push Rules</div>
        {properties.map(p => (
          <div key={p.slug} className="flex items-center gap-3 p-2 border border-slate-200 rounded"
            data-testid={`maintainx-prop-${p.slug}`}>
            <div className="flex-1">
              <div className="font-black text-sm">{p.name}</div>
              <div className="text-[10px] text-slate-500">{p.subtitle}</div>
            </div>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!pushEnabled[p.slug]}
                onChange={(e) => setPushEnabled({...pushEnabled, [p.slug]: e.target.checked})}
                data-testid={`maintainx-toggle-${p.slug}`}/>
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-700">
                Push to MaintainX
              </span>
            </label>
          </div>
        ))}
      </div>

      <div className="p-4 border-2 border-dashed border-slate-300 bg-slate-50 rounded space-y-2">
        <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">What will get pushed (once activated)</div>
        <ul className="text-xs space-y-1 list-disc pl-5 text-slate-700">
          <li>Every new tenant-submitted request (with photos, unit, priority)</li>
          <li>Status changes: assigned → in-progress → completed</li>
          <li>Completion notes + photos + invoice PDF (when marked done)</li>
          <li>One-way <b>write only</b> — FR never reads back or overwrites MaintainX state</li>
        </ul>
      </div>
    </div>
  );
}
